<template>
    <v-menu v-model="showMenu"
        :offset-y="display_type === 'chip'"
        :close-on-content-click="false"
        :disabled="readOnly"
    >
        <template v-slot:activator="{ on, attrs }">
            <v-chip v-if="display_type === 'chip'" label large class="mx-1 px-1 no-shrink" v-bind="attrs" v-on="on" :color="color" :text-color="colorContrast">
                <table>
                    <tr>
                        <td class="caption">{{$style.label.short(name + (product ? '!product' : '!air'))}}</td>
                        <td class="ps-2 pv-display" rowspan="2">
                            <span v-show="product">{{productPvFixed}}</span>
                            <span v-show="!product">{{airPvFixed}}</span>
                            <span class="units">{{units}}</span>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <span v-if="enable">{{svFixed}}<span class="units">{{units}}</span></span>
                            <span v-else>{{$t("Off")}}</span>
                        </td>
                    </tr>
                </table>
            </v-chip>
            <v-card
                v-if="display_type === 'card'"
                min-width="300px"
                height="100%"
                v-bind="attrs"
                v-on="on"
                class="d-flex flex-column"
                color="secondary"
            >
                <v-card-title class="pb-0">
                    {{$style.label.full(name + (product ? '!product' : '!air'))}}
                </v-card-title>
                <v-spacer></v-spacer>
                <v-card-text class="pt-0">
                    <v-row no-gutters>
                        <v-col style="position: relative;">
                            <div style="position: absolute; bottom: 0px; left: 0px;">
                                <span v-if="enable" class="display-2 font-weight-black">{{svFixed}}</span>
                                <span v-else class="display-2 font-weight-black">{{$t("Off")}}</span>
                                <strong v-if="enable">{{units}}</strong>
                                <div class="caption grey--text">{{$t("Set Value")}}</div>
                            </div>
                        </v-col>
                        <v-col align="right">
                            <div v-bind:class="airStyle">
                                <span class="caption">{{$t('Air')}}</span>
                                <span class="display-1 font-weight-black">{{airPvFixed}}</span>
                                <strong>{{units}}</strong>
                            </div>
                            <div v-bind:class="prodStyle">
                                <span class="caption">{{$t('Product')}}</span>
                                <span class="display-1 font-weight-black">{{productPvFixed}}</span>
                                <strong>{{units}}</strong>
                            </div>
                            <div class="caption grey--text">{{$t("Process Value")}}</div>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-progress-linear color="red" height="18" :value="powerHeat">{{$t("Heat")}}: {{powerHeat}}%</v-progress-linear>
                    </v-row>
                    <v-row no-gutters>
                        <v-progress-linear color="blue" height="18" :value="powerCool">{{$t("Cool")}}: {{powerCool}}%</v-progress-linear>
                    </v-row>
                </v-card-text>
            </v-card>
        </template>
        <edit-loop
            v-if="myConstantValid"
            :name="name"
            :valid="myConstantValid"
            :data.sync="myConstant[name]"
            showActions
            @close="showMenu = false"
            @apply="apply"
        ></edit-loop>
        <v-card v-else>
            <v-card-text>
                {{$t('Must be in Constant mode.')}}
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="showMenu = false">{{$t('Close')}}</v-btn>
            </v-card-actions>
        </v-card>
    </v-menu>
</template>

<script>
import "@/assets/global.css";
import EspecChamberApi from '@/services/EspecChamberApi.js';
import { cloneDeep, roundTo, contrastColorString, mutexHandler } from '@/services/Utils.js';
import EditLoop from './loopProdEdit.vue';

export default {
    props: {
        name: {type: String, required: true},
        enable: {type: Boolean, required: true},
        product: {type: Boolean, required: true},
        set_value: {type: Number, required: false},
        air_process_value: {type: Number, required: false},
        product_process_value: {type: Number, required: false},
        power: {type: Number, required: false},
        units: {type: String, default: ''},
        display_type: {type: String, default: 'chip'}
    },
    components: { EditLoop },
    data: () => ({
        showMenu: false,
        myConstant: null,
        myConstantValid: false,
        myConstantKey: '',
        color: 'secondary'
    }),
    computed: {
        powerHeat () { return roundTo(this.power < 0 ? 0 : this.power, 0) },
        powerCool () { return roundTo(this.power > 0 ? 0 : Math.abs(this.power), 0) },
        svFixed () { return this.formatValue(this.set_value); },
        airPvFixed () { return this.formatValue(this.air_process_value) },
        productPvFixed() { return this.formatValue(this.product_process_value) },
        prodStyle () { return this.pvStyle(!this.product) },
        airStyle () { return this.pvStyle(this.product) },
        readOnly () { return !this.$store.getters['auth/hasRole']('constants', true) || !this.$store.getters['constants/hasConstants']; },
        colorContrast () {
            const theme = this.$vuetify.theme.dark ? this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
            return contrastColorString(theme[this.color] || this.color);
        },
        my_constant_str () { return JSON.stringify(this.myConstant); },
    },
    methods: {
        formatValue (val) {
            if (val !== null && val !== undefined)
                return this.$style.value(this.name, val);
            return this.$t('Error');
        },
        apply () {
            this.showMenu = false;
            const data = {[this.name]: this.myConstant[this.name]};
            EspecChamberApi.putConstant(this.myConstantKey, data).then(r => {
                this.$store.commit('constants/updateByKey', r)
                this.$store.commit('queue/push', {
                    promise: () => EspecChamberApi.getConditions({cache: 'refresh'}),
                    then: r => this.$store.commit('conditions/update', r.conditions)
                });
            });
        },
        setMenu () {
            this.myConstantKey = this.$store.getters['conditions/constKey'] || this.$store.getters['constants/firstID'];
            this.myConstantValid = !!this.myConstantKey;
            this.myConstant = this.myConstantKey ? cloneDeep(this.$store.getters['constants/loops'](this.myConstantKey)) : null;
        },
        pvStyle (activate) {
            return { 'de-emphasize': activate };
        }
    },
    watch: {
        showMenu () { return this.setMenu() },
        my_constant_str (nv, ov) { mutexHandler(this, this.myConstant, nv, ov); },
    },
    mounted () { this.setMenu() }
}
</script>

<style scoped>
.pv-display {
    font-size: 1.5em;
}
.units {
    font-size: 0.6em;
}
.de-emphasize {
    opacity: 0.3;
}
</style>